import type { MetaFunction } from 'react-router';

export const handle = {
  title: 'Terms of Service - Pavone',
  description: 'Read our terms of service and understand your rights and responsibilities when using Pavone.',
};

export const meta: MetaFunction = () => {
  return [
    { title: 'Terms of Service - Pavone' },
    { name: 'description', content: 'Read our terms of service and understand your rights and responsibilities when using Pavone.' },
  ];
};

export default function Terms() {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow-lg rounded-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">
            Terms of Service
          </h1>
          <p className="text-sm text-gray-600 mb-8">Last updated: 8.10.2025</p>

          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                1. Acceptance of Terms
              </h2>
              <p className="text-gray-700">
                By accessing or using Pavone ("the Service"), you agree to be
                bound by these Terms of Service ("Terms"). If you disagree with
                any part of these terms, you may not access the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                2. Description of Service
              </h2>
              <p className="text-gray-700 mb-4">
              Pavone is a video analysis and speech improvement platform that
                uses artificial intelligence to provide feedback on
                communication skills, presentation abilities, and speech
                patterns.
              </p>
              <p className="text-gray-700">
                Our service includes video recording, transcription, AI
                analysis, personalized feedback, and progress tracking features.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                3. User Accounts
              </h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>You must create an account to use our Service</li>
                <li>
                  You are responsible for maintaining the confidentiality of
                  your account credentials
                </li>
                <li>
                  You must provide accurate and complete information when
                  creating your account
                </li>
                <li>
                  You are responsible for all activities that occur under your
                  account
                </li>
                <li>
                  You must notify us immediately of any unauthorized use of your
                  account
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                4. User Content and Data
              </h2>

              <h3 className="text-xl font-medium text-gray-800 mb-3">
                4.1 Your Content
              </h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                <li>
                  You retain ownership of all video recordings and content you
                  upload
                </li>
                <li>
                  You grant us a limited license to process your content to
                  provide our services
                </li>
                <li>
                  You are responsible for the content you upload and must ensure
                  you have the right to upload it
                </li>
                <li>
                  You must not upload content that violates any laws or
                  third-party rights
                </li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">
                4.2 Prohibited Content
              </h3>
              <p className="text-gray-700 mb-2">
                You may not upload content that:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Contains illegal, harmful, or offensive material</li>
                <li>Violates intellectual property rights</li>
                <li>Contains personal information of others without consent</li>
                <li>Is intended to harass, threaten, or intimidate</li>
                <li>Contains malware or malicious code</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                5. Subscription and Payment
              </h2>

              <h3 className="text-xl font-medium text-gray-800 mb-3">
                5.1 Subscription Plans
              </h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                <li>
                  We offer free and paid subscription plans with different
                  features and limits
                </li>
                <li>
                  Subscription fees are billed in advance on a recurring basis
                </li>
                <li>All payments are processed securely through Stripe</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">
                5.2 Billing and Refunds
              </h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>
                  Subscription fees are non-refundable except as required by law
                </li>
                <li>
                  You can cancel your subscription at any time through your
                  account settings
                </li>
                <li>
                  Cancellation takes effect at the end of your current billing
                  period
                </li>
                <li>
                  We reserve the right to change subscription prices with 30
                  days notice
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                6. Acceptable Use
              </h2>
              <p className="text-gray-700 mb-4">You agree not to:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>
                  Use the Service for any unlawful purpose or in violation of
                  any laws
                </li>
                <li>
                  Attempt to gain unauthorized access to our systems or other
                  users' accounts
                </li>
                <li>Interfere with or disrupt the Service or servers</li>
                <li>Use automated scripts or bots to access the Service</li>
                <li>Reverse engineer or attempt to extract source code</li>
                <li>Share your account credentials with others</li>
                <li>
                  Use the Service to compete with us or develop competing
                  products
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                7. Privacy and Data Protection
              </h2>
              <p className="text-gray-700 mb-4">
                Your privacy is important to us. Our collection and use of your
                information is governed by our Privacy Policy, which is
                incorporated into these Terms by reference.
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>
                  We process your data in accordance with GDPR and other
                  applicable privacy laws
                </li>
                <li>
                  You have rights regarding your personal data as outlined in
                  our Privacy Policy
                </li>
                <li>
                  We implement appropriate security measures to protect your
                  data
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                8. Intellectual Property
              </h2>

              <h3 className="text-xl font-medium text-gray-800 mb-3">
                8.1 Our Rights
              </h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                <li>
                  The Service and its original content, features, and
                  functionality are owned by us
                </li>
                <li>
                  Our trademarks, logos, and brand features are protected by
                  intellectual property laws
                </li>
                <li>
                  You may not use our intellectual property without written
                  permission
                </li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">
                8.2 Your Rights
              </h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>You retain ownership of your original content</li>
                <li>
                  AI-generated analysis and feedback are provided as a service
                </li>
                <li>
                  You may export your data as outlined in our Privacy Policy
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                9. Service Availability
              </h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>
                  We strive to maintain high availability but cannot guarantee
                  uninterrupted service
                </li>
                <li>
                  We may suspend or discontinue the Service with reasonable
                  notice
                </li>
                <li>
                  We may perform maintenance that temporarily affects service
                  availability
                </li>
                <li>
                  We are not liable for any downtime or service interruptions
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                10. Disclaimers and Limitations
              </h2>

              <h3 className="text-xl font-medium text-gray-800 mb-3">
                10.1 Service Disclaimers
              </h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                <li>
                  The Service is provided "as is" without warranties of any kind
                </li>
                <li>
                  AI analysis is for educational purposes and may not be 100%
                  accurate
                </li>
                <li>
                  We do not guarantee specific outcomes from using our Service
                </li>
                <li>You use the Service at your own risk</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">
                10.2 Limitation of Liability
              </h3>
              <p className="text-gray-700">
                To the maximum extent permitted by law, we shall not be liable
                for any indirect, incidental, special, consequential, or
                punitive damages, including but not limited to loss of profits,
                data, or goodwill.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                11. Indemnification
              </h2>
              <p className="text-gray-700">
                You agree to indemnify and hold us harmless from any claims,
                damages, or expenses arising from your use of the Service,
                violation of these Terms, or infringement of any rights of
                another.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                12. Termination
              </h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>
                  You may terminate your account at any time through your
                  account settings
                </li>
                <li>
                  We may terminate or suspend your account for violations of
                  these Terms
                </li>
                <li>
                  Upon termination, your right to use the Service ceases
                  immediately
                </li>
                <li>
                  Data retention after termination is governed by our Privacy
                  Policy
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                13. Governing Law
              </h2>
              <p className="text-gray-700">
                These Terms are governed by and construed in accordance with the
                laws of Slovenia, without regard to conflict of law principles.
                Any disputes shall be resolved in the courts of Slovenia.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                14. Changes to Terms
              </h2>
              <p className="text-gray-700">
                We reserve the right to modify these Terms at any time. We will
                notify users of material changes via email or through the
                Service. Continued use after changes constitutes acceptance of
                the modified Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                15. Contact Information
              </h2>
              <div className="text-gray-700">
                <p className="mb-2">
                  <strong>Support:</strong> info@pavone.ai
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                16. Severability
              </h2>
              <p className="text-gray-700">
                If any provision of these Terms is found to be unenforceable,
                the remaining provisions will remain in full force and effect.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
